import { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Avatar,
  Upload,
  Row,
  Col,
  Divider,
} from "antd";
import { UserOutlined, UploadOutlined, SaveOutlined } from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import apiClient from "../utils/apiClient";

const ProfileManagement = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({});

  useEffect(() => {
    if (user) {
      setProfileData(user);
      form.setFieldsValue({
        name: user.name,
        email: user.email,
        phone: user.phone || "",
        department: user.department || "",
        bio: user.bio || "",
      });
    }
  }, [user, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await apiClient.put("/users/profile", values);

      if (response.data) {
        setProfileData(
  response.data?.data?.user || {}
);
        message.success("Profile updated successfully!");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      message.error("Failed to update profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (info) => {
    if (info.file.status === "uploading") {
      setLoading(true);
      return;
    }

    if (info.file.status === "done") {
      setLoading(false);
      message.success("Avatar updated successfully!");
    } else if (info.file.status === "error") {
      setLoading(false);
      message.error("Failed to upload avatar.");
    }
  };

  return (
    <div style={{ padding: "24px", maxWidth: "800px", margin: "0 auto" }}>
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <UserOutlined style={{ fontSize: "20px", color: "#1890ff" }} />
            <span>Profile Management</span>
          </div>
        }
        style={{ borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
      >
        <Row gutter={[24, 24]}>
          {/* Avatar Section */}
          <Col xs={24} md={8} style={{ textAlign: "center" }}>
            <div style={{ marginBottom: "16px" }}>
              <Avatar
                size={120}
                src={profileData.avatar}
                icon={<UserOutlined />}
                style={{
                  border: "4px solid #f0f0f0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              />
            </div>
            <Upload
              name="avatar"
              action="/users/upload-avatar"
              headers={{
                authorization: `Bearer ${localStorage.getItem("token")}`,
              }}
              showUploadList={false}
              onChange={handleAvatarUpload}
            >
              <Button icon={<UploadOutlined />} loading={loading}>
                Change Avatar
              </Button>
            </Upload>

            <Divider />

            <div style={{ textAlign: "left" }}>
              <p>
                <strong>Role:</strong> {user?.role || "N/A"}
              </p>
              <p>
                <strong>Status:</strong>
                <span
                  style={{
                    color: user?.isActive ? "#52c41a" : "#ff4d4f",
                    marginLeft: "8px",
                  }}
                >
                  {user?.isActive ? "Active" : "Inactive"}
                </span>
              </p>
              <p>
                <strong>Verified:</strong>
                <span
                  style={{
                    color: user?.isVerified ? "#52c41a" : "#faad14",
                    marginLeft: "8px",
                  }}
                >
                  {user?.isVerified ? "Yes" : "Pending"}
                </span>
              </p>
            </div>
          </Col>

          {/* Profile Form */}
          <Col xs={24} md={16}>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              style={{ width: "100%" }}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Full Name"
                    name="name"
                    rules={[
                      { required: true, message: "Please enter your name" },
                      { min: 2, message: "Name must be at least 2 characters" },
                    ]}
                  >
                    <Input placeholder="Enter your full name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Email Address"
                    name="email"
                    rules={[
                      { required: true, message: "Please enter your email" },
                      { type: "email", message: "Please enter a valid email" },
                    ]}
                  >
                    <Input placeholder="Enter your email" disabled />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Phone Number" name="phone">
                    <Input placeholder="Enter your phone number" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Department" name="department">
                    <Input placeholder="Enter your department" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Bio" name="bio">
                <Input.TextArea
                  rows={4}
                  placeholder="Tell us about yourself..."
                  maxLength={500}
                  showCount
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SaveOutlined />}
                  size="large"
                  style={{ borderRadius: "6px" }}
                >
                  Update Profile
                </Button>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default ProfileManagement;
